import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from 'config/firebase';
import { collection, query, where, onSnapshot, getDocs, writeBatch, doc, serverTimestamp } from 'firebase/firestore';

/**
 * Firestore collection path for enrichment jobs.
 */
const COLLECTION_PATH = 'productCatalog/topicz/enrichmentJobs';

/**
 * Hook for tracking enrichment job progress in real-time.
 *
 * Features:
 * - Real-time job updates via Firestore onSnapshot
 * - Batches SKUs into groups of 30 to avoid Firestore 'in' query limit
 * - Tracks overall progress (completed/total/failed)
 * - Provides getSkipCounts for modal skip count calculation
 * - Provides cancelJobs for cancelling pending jobs
 * - Cleans up listeners on unmount
 *
 * @returns {object} Hook state and methods
 */
export function useEnrichmentProgress() {
    const [jobs, setJobs] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState({ completed: 0, total: 0, failed: 0 });
    const unsubscribersRef = useRef([]);

    /**
     * Batch SKUs into groups of 30 (Firestore 'in' query limit).
     * @param {string[]} skuList - Array of SKUs to batch
     * @returns {string[][]} Array of SKU batches
     */
    const batchSkus = useCallback((skuList) => {
        const batches = [];
        for (let i = 0; i < skuList.length; i += 30) {
            batches.push(skuList.slice(i, i + 30));
        }
        return batches;
    }, []);

    /**
     * Start listening to job updates for given SKUs.
     * @param {string[]} skuList - Array of SKUs to track
     */
    const startListening = useCallback((skuList) => {
        if (!skuList || skuList.length === 0) return;

        // Clean up existing listeners
        unsubscribersRef.current.forEach(unsub => unsub());
        unsubscribersRef.current = [];

        const batches = batchSkus(skuList);

        batches.forEach((batch) => {
            const jobsRef = collection(db, COLLECTION_PATH);
            const q = query(jobsRef, where('sku', 'in', batch));

            const unsub = onSnapshot(q, (snapshot) => {
                setJobs(prev => {
                    const updated = { ...prev };
                    snapshot.docChanges().forEach(change => {
                        const job = { id: change.doc.id, ...change.doc.data() };
                        if (change.type === 'removed') {
                            delete updated[job.sku];
                        } else {
                            // When multiple jobs exist for the same SKU, prefer the most recent
                            const existing = updated[job.sku];
                            if (!existing) {
                                updated[job.sku] = job;
                            } else {
                                // Prefer the job with the latest createdAt timestamp
                                const jobTime = job.createdAt?.toMillis?.() || job.createdAt?.getTime?.() || 0;
                                const existingTime = existing.createdAt?.toMillis?.() || existing.createdAt?.getTime?.() || 0;
                                if (jobTime >= existingTime) {
                                    updated[job.sku] = job;
                                }
                            }
                        }
                    });
                    return updated;
                });
            }, (error) => {
                console.error('Error listening to enrichment jobs:', error);
            });

            unsubscribersRef.current.push(unsub);
        });
    }, [batchSkus]);

    /**
     * Stop listening to job updates and clear state.
     */
    const stopListening = useCallback(() => {
        unsubscribersRef.current.forEach(unsub => unsub());
        unsubscribersRef.current = [];
        setJobs({});
    }, []);

    /**
     * Calculate progress from current jobs state.
     */
    useEffect(() => {
        const jobList = Object.values(jobs);
        const total = jobList.length;
        const completed = jobList.filter(j =>
            ['completed', 'partial', 'approved', 'pushed'].includes(j.status)
        ).length;
        const failed = jobList.filter(j =>
            ['failed', 'partial'].includes(j.status)
        ).length;
        const processing = jobList.some(j =>
            ['pending', 'processing'].includes(j.status)
        );

        setProgress({ completed, total, failed });
        setIsProcessing(processing);
    }, [jobs]);

    /**
     * Cancel pending/processing jobs.
     * @param {string[]} jobIds - Array of job document IDs to cancel
     */
    const cancelJobs = useCallback(async (jobIds) => {
        if (!jobIds || jobIds.length === 0) return;

        const batch = writeBatch(db);

        for (const jobId of jobIds) {
            const jobRef = doc(db, COLLECTION_PATH, jobId);
            batch.update(jobRef, {
                status: 'cancelled',
                cancelledAt: serverTimestamp(),
                error: 'Cancelled by user'
            });
        }

        await batch.commit();
    }, []);

    /**
     * Get skip counts for modes (check completed enrichment in existing jobs).
     * Used by EnrichmentModeModal to show how many products will be skipped per mode.
     *
     * @param {string[]} skuList - Array of SKUs to check
     * @param {string[]} modes - Array of mode names to check
     * @returns {Promise<object>} Object with mode names as keys and skip counts as values
     */
    const getSkipCounts = useCallback(async (skuList, modes) => {
        const skipCounts = {};
        modes.forEach(mode => { skipCounts[mode] = 0; });

        if (!skuList || skuList.length === 0) {
            return skipCounts;
        }

        const batches = batchSkus(skuList);

        for (const batch of batches) {
            const jobsRef = collection(db, COLLECTION_PATH);
            const q = query(jobsRef, where('sku', 'in', batch));
            const snapshot = await getDocs(q);

            snapshot.docs.forEach(docSnapshot => {
                const job = docSnapshot.data();
                modes.forEach(mode => {
                    // Check if this mode was already completed for this job
                    if (job.results?.[mode]?.status === 'completed' ||
                        job.results?.[mode]?.status === 'approved' ||
                        job.results?.[mode]?.status === 'pushed') {
                        skipCounts[mode]++;
                    }
                });
            });
        }

        return skipCounts;
    }, [batchSkus]);

    /**
     * Get pending job IDs that can be cancelled.
     * @returns {string[]} Array of job IDs with pending/processing status
     */
    const getPendingJobIds = useCallback(() => {
        return Object.values(jobs)
            .filter(j => ['pending', 'processing'].includes(j.status))
            .map(j => j.id);
    }, [jobs]);

    /**
     * Cleanup on unmount.
     */
    useEffect(() => {
        return () => {
            unsubscribersRef.current.forEach(unsub => unsub());
        };
    }, []);

    return {
        jobs,
        jobList: Object.values(jobs),
        isProcessing,
        progress,
        startListening,
        stopListening,
        cancelJobs,
        getSkipCounts,
        getPendingJobIds
    };
}

export default useEnrichmentProgress;
